// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V1 lib imports
const WindowLib = require('client/lib/window')
// Client V2 common imports
const useClientEffect = require('client-v2/common/hooks/client-effect')
const CardGrid = require('client-v2/common/components/card-grid')
const tag = require('client-v2/common/utils/hyperscript')

const bem = BemEntity('Feed')

function Feed(props) {
  const blockRef = React.useRef()
  const { onMoreClick, onPaginate } = props
  const onTrigger = React.useCallback(
    () => {
      if (onPaginate) {
        onPaginate()
      }
      onMoreClick()
    },
    [ onMoreClick, onPaginate ]
  )

  useInfiniteScroll({
    disabled: !props.isMoreAvailable,
    domRef: blockRef,
    onTrigger
  })

  const renderLeading = props.leadingComponent
  ? () => tag(props.leadingComponent, props)
  : null
  const renderTrailing = props.trailingComponent
  ? () => tag(props.trailingComponent, props)
  : null

  return (
    tag('div', {
      ref: blockRef,
      className: classNames(
        props.className,
        bem.block()
      )
    },
      props.emptyMessage
      ? tag(props.emptyMessage, {
        ...props,
        className: bem.element('empty-message')
      })
      : tag(CardGrid, {
        ...props,
        className: bem.element('grid'),
        renderLeading,
        renderTrailing
      })
    )
  )
}

Feed.propTypes = {
  // Flags
  isMoreAvailable: PropTypes.bool,
  // Values
  emptyMessage: PropTypes.elementType,
  // Components
  leadingComponent: PropTypes.elementType,
  trailingComponent: PropTypes.elementType,
  // Callbacks
  onMoreClick: PropTypes.func.isRequired
}

module.exports = Feed

// Internal hook

/**
 * @TODO Refactor this into `client-v2/common/hooks/infinite-scroll.js`
 *
 * @param {Object} options
 * @param {boolean} options.disabled
 * @param {React.RefObject<HTMLElement>} options.domRef
 * @param {number} [options.scrollThrottleFrequency]
 * @param {function(): void} options.onTrigger
 * @returns {void}
 */
function useInfiniteScroll({
  disabled,
  domRef,
  scrollThrottleFrequency = 100 /* (milliseconds) */,
  onTrigger
}) {
  useClientEffect(
    () => {
      if (disabled) {
        return
      }

      const scrollEl = WindowLib.getScrollParent(domRef.current)
      const scrollTarget = scrollEl === document.body ? window : scrollEl

      function onScroll() {
        const scrollTop = scrollTarget.scrollY || scrollEl.scrollTop
        const viewportHeight = scrollTarget.innerHeight || scrollEl.clientHeight
        const contentHeight = scrollEl.scrollHeight
        const scrollBottom = scrollTop + viewportHeight
        const triggerThreshold = contentHeight - viewportHeight
        if (scrollBottom > triggerThreshold) {
          onTrigger()
        }
      }

      const onScrollThrottled = _.throttle(
        onScroll,
        scrollThrottleFrequency,
        { trailing: false }
      )

      scrollTarget.addEventListener('scroll', onScrollThrottled)

      return () => {
        scrollTarget.removeEventListener('scroll', onScrollThrottled)
      }
    },
    [ disabled, domRef, scrollThrottleFrequency, onTrigger ]
  )
}
